import React, { useCallback } from 'react';
import { AutoField, FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

import type { SelectOption } from '../types';

type RadioFieldProps = {
  field: { label?: string; labelIcon?: React.ReactNode };
  name: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

export function RadioField(options: readonly SelectOption[], fallbackValue?: string) {
  return function RadioFieldWidget({ field, name, value, onChange, readOnly }: RadioFieldProps): JSX.Element {
    const displayedValue = value != null && value !== '' ? value : fallbackValue ?? '';

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (readOnly) return;
        // A label click also dispatches a click on its input. Handle only the latter to avoid saving twice.
        const input = e.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
        let clickedVal: string | undefined;
        try {
          const parsed = JSON.parse(input.value) as { value?: unknown } | null;
          if (parsed && typeof parsed.value === 'string') {
            clickedVal = parsed.value;
          }
        } catch {
          if (input.value) {
            clickedVal = input.value;
          }
        }
        if (clickedVal !== undefined && clickedVal === displayedValue) {
          onChange(clickedVal);
        }
      },
      [displayedValue, onChange, readOnly]
    );

    return (
      <div onClickCapture={handleClick} data-testid={`radio-field-${name}`}>
        <FieldLabel label={field.label ?? name} icon={field.labelIcon} readOnly={readOnly} el='div'>
          <AutoField
            field={{
              type: 'radio',
              options: options as SelectOption[],
            }}
            value={displayedValue}
            onChange={onChange}
            readOnly={readOnly}
          />
        </FieldLabel>
      </div>
    );
  };
}

export function radioField(
  label: string,
  options: readonly SelectOption[],
  fallbackValue?: string,
  aiInstructions?: string
): CustomField<string> & { options: readonly SelectOption[] } {
  const optionValues = options.map((option) => option.value);
  return {
    type: 'custom',
    label,
    options,
    key: `radio-${optionValues.join('|')}${fallbackValue ? `-${fallbackValue}` : ''}`,
    render: RadioField(options, fallbackValue),
    ai: {
      schema: { type: 'string', enum: optionValues },
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
